import { useRef, useState } from "react";
import { Play, MapPin, Pause } from "lucide-react";
import { TRANSFORMATIONS } from "@/lib/siteData";

const VideoTile = ({ item, index }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      try {
        await v.play();
        setPlaying(true);
      } catch (_) {
        setPlaying(false);
      }
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      data-testid={`transformation-tile-${item.id}`}
      className="video-tile group cursor-pointer"
      onClick={toggle}
    >
      <video
        ref={videoRef}
        src={item.src}
        poster={item.poster}
        muted
        playsInline
        loop
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent pointer-events-none" />

      {/* Play button */}
      <button
        type="button"
        aria-label={playing ? "Pause video" : "Play video"}
        data-testid={`transformation-play-${item.id}`}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center transition-all ${
          playing ? "opacity-0 group-hover:opacity-100" : "opacity-100 play-pulse"
        }`}
      >
        {playing ? (
          <Pause className="w-7 h-7" />
        ) : (
          <Play className="w-7 h-7 ml-1" fill="currentColor" />
        )}
      </button>

      {/* Caption */}
      <div className="absolute bottom-0 inset-x-0 p-5 text-white pointer-events-none">
        <div className="text-xs text-green-300 font-mono mb-1">CLIP 0{index + 1}</div>
        <h3
          className="text-lg sm:text-xl font-bold leading-tight"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {item.title}
        </h3>
        <div className="mt-1 flex items-center gap-1 text-xs text-white/80">
          <MapPin className="w-3 h-3" /> {item.location}
        </div>
        <p className="mt-2 text-sm text-white/80 leading-snug max-w-xs">
          {item.caption}
        </p>
      </div>
    </div>
  );
};

export const Transformations = () => {
  return (
    <section
      id="work"
      data-testid="transformations-section"
      className="py-20 sm:py-28 lg:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest">
              See the transformation
            </span>
            <h2
              data-testid="transformations-headline"
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Watch us work: real results for local homeowners.
            </h2>
          </div>
          <p className="text-slate-600 max-w-md text-base lg:text-lg">
            Tap any clip to play a short, satisfying before-and-after from a recent
            local job.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {TRANSFORMATIONS.map((t, i) => (
            <VideoTile key={t.id} item={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Transformations;
